/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ImpulseElement, property, registerElement, target } from '@ambiki/impulse';
import trimCanvas from 'helpers/trim_canvas';
import SignaturePad from 'signature_pad';

@registerElement('signature-pad')
export default class SignaturePadElement extends ImpulseElement {
  /**
   * Whether the signature value is required or not.
   */
  @property({ type: Boolean }) required = false;

  /**
   * Whether free-hand drawing is disabled or not.
   */
  @property({ type: Boolean }) drawable = true;

  /**
   * Whether to trim transparent edges from the signature before saving.
   */
  @property({ type: Boolean }) trim = true;

  @target() input: HTMLInputElement;
  @target() hiddenField?: HTMLInputElement;

  private signaturePad: SignaturePad;
  private abortController = new AbortController();

  connected() {
    const { signal } = this.abortController;

    this.setCanvasDrawCapability(this.drawable);
    this.calculateDimensions();
    this.canvas.style.padding = '0';
    this.signaturePad = new SignaturePad(this.canvas, { minWidth: 1, maxWidth: 1 });
    this.signaturePad.addEventListener('endStroke', this.onEndStroke.bind(this), { signal });
    window.addEventListener('resize', this.onWindowResize.bind(this), { signal });
    this.setCustomValidity(this.required);
  }

  disconnected() {
    this.clear();
    this.signaturePad.off();
    this.abortController.abort();
  }

  drawableChanged(value: boolean) {
    this.setCanvasDrawCapability(value);
  }

  // Force SeptemberMornings font to load
  inputConnected() {
    if (this.ctx) {
      this.ctx.font = 'normal normal normal 4.5rem SeptemberMornings';
      this.ctx.fillText('', 0, 0);
    }
  }

  requiredChanged(value: boolean) {
    this.setCustomValidity(value);
  }

  type(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.ctx) return;

    this.setText(this.input.value);
  }

  setText(text: string) {
    if (!this.ctx) return;

    this.ctx.font = 'normal normal normal 5.5rem SeptemberMornings';
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    const xAxis = this.canvas.width / 2;
    const yAxis = this.canvas.height / 2 + 12; // Add some offset to account for text height.
    const trimmedText = text.trim();

    this.ctx.textAlign = 'center';
    this.ctx.fillText(trimmedText, xAxis, yAxis, this.canvas.width);

    if (trimmedText.length === 0) {
      this.updateHiddenField('');
    } else {
      this.updateHiddenField();
    }
  }

  clear() {
    this.signaturePad.clear();
    this.input.value = '';
    this.updateHiddenField('');
    this.emit('clear');
  }

  calculateDimensions() {
    const { width, height } = this.canvas.parentElement!.getBoundingClientRect();
    this.canvas.setAttribute('width', `${width}px`);
    this.canvas.setAttribute('height', `${height}px`);
    this.canvas.style.height = `${height}px`;
  }

  private onEndStroke() {
    this.updateHiddenField();
  }

  // Handle the case where the size of the window changes. Otherwise, the mouse pointer will not match
  // the signature line.
  private onWindowResize() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    this.canvas.width = this.canvas.offsetWidth * ratio;
    this.canvas.height = this.canvas.offsetHeight * ratio;
    if (this.ctx) {
      this.ctx.scale(ratio, ratio);
    }
    this.clear();
  }

  private updateHiddenField(rawValue?: string) {
    const value = rawValue ?? this.getSignatureDataURL();

    this.setCustomValidity(this.required && !value);
    this.emit('signed', { detail: { value } });
    if (this.hiddenField) {
      this.hiddenField.value = value;
    }
  }

  /**
   * Gets the signature as a data URL, optionally trimming transparent edges.
   *
   * @returns The signature as a base64-encoded data URL
   */
  private getSignatureDataURL(): string {
    if (!this.trim) {
      return this.signaturePad.toDataURL();
    }

    // Create a temporary canvas to avoid modifying the original
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    if (!tempCtx) {
      return this.signaturePad.toDataURL();
    }

    // Copy the current canvas content to the temporary canvas
    tempCanvas.width = this.canvas.width;
    tempCanvas.height = this.canvas.height;
    tempCtx.drawImage(this.canvas, 0, 0);

    // Trim the temporary canvas
    try {
      trimCanvas(tempCanvas);
      return tempCanvas.toDataURL();
    } catch (error) {
      return this.signaturePad.toDataURL();
    }
  }

  private setCustomValidity(value: boolean) {
    this.input.setCustomValidity(value ? 'Please type or draw your signature' : '');
  }

  private setCanvasDrawCapability(value: boolean) {
    this.canvas.style.pointerEvents = value ? 'auto' : 'none';
  }

  /**
   * Returns an object that provides methods and properties for drawing and manipulating images and graphics on a
   * canvas element in a document. A context object includes information about colors, line widths, fonts, and other
   * graphic parameters that can be drawn on a canvas.
   */
  get ctx() {
    return this.canvas.getContext('2d');
  }

  get canvas() {
    return this.querySelector('canvas')!;
  }
}
